import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { Button } from '../../components/button/Button';
import {
  Checkbox,
  Dialog,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from '@mui/material';
import IFormError from '../../interfaces/IFormError';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { IUpdateValuesInBatchParams } from '../../interfaces/IUpdateValuesInBatchParams';
import getValidationError from '../../util/getValidationError';
import { useOrderItemService } from '../../services/useOrderItemService';
import { useToastr } from '../../hooks/useToastr';
import { BackdropCustom } from '../../components/backdrop/Backdrop';

interface IProps {
  openDialog: boolean;
  handleClose: () => void;
  recalculateOrder: (orderId: string) => Promise<void>;
  orderId: string;
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  decimalScale: number;
}

const NumericFormatCustom = forwardRef<NumericFormatProps, CustomProps>(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={values => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator={'.'}
      decimalScale={props.decimalScale}
      decimalSeparator=","
      valueIsNumericString
    />
  );
});

const OrderItemUpdateValues: React.FC<IProps> = ({ openDialog, handleClose, recalculateOrder, orderId }) => {
  const { updateValuesInBatchParams } = useOrderItemService();
  const toastr = useToastr();

  const [offerPercentage, setOfferPercentage] = useState('');
  const [additionPercentage, setAdditionPercentage] = useState('');
  const [offerPercentageChecked, setOfferPercentageChecked] = useState(true);
  const [additionPercentageChecked, setAdditionPercentageChecked] = useState(false);

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<IFormError>({});

  const handleConfirmation = useCallback(async () => {
    setFormErrors({});
    setLoading(true);

    try {
      const data: IUpdateValuesInBatchParams = {
        offerPercentage: Number(offerPercentage.replace(',', '.')),
        additionPercentage: Number(additionPercentage.replace(',', '.')),
        offerPercentageChecked,
        additionPercentageChecked,
      };

      const schema = Yup.object().shape({
        offerPercentage: Yup.number()
          .lessThan(100.00001, 'O valor deve ser menor ou igual a 100')
          .min(0, 'O valor deve ser maior ou igual a 0'),
        additionPercentage: Yup.number()
          .lessThan(100.00001, 'O valor deve ser menor ou igual a 100')
          .min(0, 'O valor deve ser maior ou igual a 0'),
        offerPercentageChecked: Yup.boolean(),
        additionPercentageChecked: Yup.boolean(),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      handleClose();
      await updateValuesInBatchParams(orderId, data).then(async () => {
        setLoading(false);
        await recalculateOrder(orderId);
      });
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(error?.message || 'Contate a equipe de suporte');
    } finally {
      setLoading(false);
    }
  }, [
    offerPercentage,
    additionPercentage,
    offerPercentageChecked,
    additionPercentageChecked,
    updateValuesInBatchParams,
    orderId,
    recalculateOrder,
    handleClose,
    toastr,
  ]);

  const resetForm = () => {
    setOfferPercentage('');
    setAdditionPercentage('');
    setOfferPercentageChecked(true);
    setAdditionPercentageChecked(false);
    setFormErrors({});
  };

  const closeDialog = () => {
    resetForm();
    handleClose();
  };

  useEffect(() => {
    if (openDialog) {
      resetForm();
    }
  }, [openDialog]);

  return (
    <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle sx={{ borderBottom: '#efefef solid 1px' }}>Alterar Valores</DialogTitle>

      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12} md={12} sx={{ mt: 2 }}>
            <FormControl sx={{ width: '100%' }} size="small" error={!!formErrors.offerPercentage}>
              <TextField
                style={{ width: '100%' }}
                label="% Oferta"
                autoFocus
                value={offerPercentage}
                onChange={event => setOfferPercentage(event.target.value)}
                name="value"
                size="small"
                id="distance-miles-input"
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  inputProps: {
                    decimalScale: 2,
                  },
                }}
                variant="outlined"
                error={!!formErrors.offerPercentage}
              />
              <FormHelperText>{formErrors.offerPercentage}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControl sx={{ width: '100%' }} size="small" error={!!formErrors.additionPercentage}>
              <TextField
                style={{ width: '100%' }}
                label="% Acréscimo"
                value={additionPercentage}
                onChange={event => setAdditionPercentage(event.target.value)}
                name="value"
                size="small"
                id="addition-percentage-input"
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  inputProps: {
                    decimalScale: 2,
                  },
                }}
                variant="outlined"
                error={!!formErrors.additionPercentage}
              />
              <FormHelperText>{formErrors.additionPercentage}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: '8px',
              width: '100%',
            }}
          >
            <FormControlLabel
              value={offerPercentageChecked}
              label="Alterar % Oferta"
              control={
                <Checkbox
                  onChange={() => setOfferPercentageChecked(!offerPercentageChecked)}
                  checked={Boolean(offerPercentageChecked)}
                />
              }
            />

            <FormControlLabel
              value={additionPercentageChecked}
              label="Alterar % Acréscimo"
              control={
                <Checkbox
                  onChange={() => setAdditionPercentageChecked(!additionPercentageChecked)}
                  checked={Boolean(additionPercentageChecked)}
                />
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} color="inherit">
          Fechar
        </Button>
        <Button variant="contained" onClick={handleConfirmation}>
          Confirmar
        </Button>
      </DialogActions>

      {loading && <BackdropCustom />}
    </Dialog>
  );
};

export { OrderItemUpdateValues };
